import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { CalendarPlus, ChevronDown, Copy, GitMerge, MessageCircle, MessageSquare, NotebookPen, Pencil, Phone, Play, Trash2, UserCog } from 'lucide-react'
import dayjs from 'dayjs'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import { currentLocale } from '@/lib/i18n'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent,
  DropdownMenuSubTrigger, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { StageBadge } from '@/components/shared/stage-badge'
import { ChayLuongHopXacNhan, ChayLuongThuCong } from '@/pages/automation/chay-luong-thu-cong'
import { useManualRules, type ManualRule } from '@/hooks/use-automation'
import { useAuthStore } from '@/stores/auth-store'
import { useMyPermissions, useOrgMembers } from '@/hooks/use-settings'
import { MergeContactDialog } from './merge-contact-dialog'
import { MessageContactDialog } from './message-contact-dialog'
import { planMessage } from './message-contact-lib'
import { apiError } from '@/lib/api-client'
import { cn, initials } from '@/lib/utils'
import { ContactTagPicker } from '@/components/shared/contact-tag-picker'
import { useLifecycleStages } from '@/hooks/use-lifecycle-stages'
import { formatVnd } from '@/lib/format-money'
import { useContactEvotech } from '@/hooks/use-evotech'
import { useContactOverview } from '@/hooks/use-contact-overview'
import { useUpdateContact, type ContactDetail } from '@/hooks/use-contacts'
import { useCanManageStages } from '@/components/shared/manage-links'
import { openStageManager } from '@/stores/stage-manager-store'
import { useStageMove } from '@/hooks/use-stage-move'
import { age } from './contact-detail-sections'

function Kpi({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: 'up' | 'down' }) {
  return (
    <div className="min-w-[130px] flex-1 rounded-lg border bg-card p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-xl font-bold tabular-nums">{value}</p>
      {hint && <p className={`text-xs ${tone === 'up' ? 'text-success' : tone === 'down' ? 'text-destructive' : 'text-muted-foreground'}`}>{hint}</p>}
    </div>
  )
}

/** Đầu trang chi tiết khách: ảnh, định danh, nhãn (thêm/xoá tại chỗ), chỉ số chính và nút Hành động. */
export function ContactHeader({
  contact,
  onEdit,
  onDelete,
  deleting,
  onGoTab,
}: {
  contact: ContactDetail
  onEdit: () => void
  onDelete: () => void
  deleting: boolean
  /** Chuyển tab của trang chi tiết (vd 'notes' = Ghi chú & Lịch hẹn). */
  onGoTab?: (tab: string) => void
}) {
  const { t } = useTranslation('custDetail')
  const name = contact.crmName || contact.fullName || t('header.noName')
  const member = useContactEvotech(contact.id)
  const overview = useContactOverview(contact.id)
  const update = useUpdateContact()
  const { stages } = useLifecycleStages()
  const canManageStages = useCanManageStages()
  const stageMove = useStageMove()
  const navigate = useNavigate()
  const role = useAuthStore((s) => s.user?.role)
  const canAssign = role === 'owner' || role === 'admin' || role === 'manager'
  const { data: members = [] } = useOrgMembers(canAssign)
  // "Xoá khách hàng" → BE đòi contacts.delete (Quản lý / Nhân viên mặc định không có) → ẩn hẳn.
  const { data: perms } = useMyPermissions()
  const canDelete = !!perms?.has('contacts.delete')
  const { data: manualRules } = useManualRules()
  const [mergeOpen, setMergeOpen] = useState(false)
  const [rule, setRule] = useState<ManualRule | null>(null)
  const threads = contact.conversations ?? []
  const plan = planMessage(threads)
  const [msgOpen, setMsgOpen] = useState(false)
  // Một kênh → vào thẳng hội thoại; nhiều kênh / chưa có hội thoại → hộp chọn kênh.
  const onMessage = () => (plan.kind === 'direct' ? navigate(`/conversations/${plan.conversationId}`) : setMsgOpen(true))
  const copy = (text: string, label: string) => void navigator.clipboard?.writeText(text).then(() => toast.success(t('header.copied', { label })))
  const assign = (userId: string | null, who: string) =>
    update.mutate(
      { id: contact.id, data: { assignedUserId: userId } as never },
      { onSuccess: () => toast.success(userId ? t('header.assigned', { name, who }) : t('header.unassigned')), onError: (e) => toast.error(apiError(e)) },
    )

  const m = member.data && member.data.configured && member.data.linked ? member.data.member : null
  const ov = overview.data
  const a = age(contact.birthday)
  const tags = contact.tags ?? []

  const copyId = () => {
    void navigator.clipboard?.writeText(contact.id).then(() => toast.success(t('header.idCopied')))
  }

  return (
    <div className="rounded-xl border bg-card p-5">
      <div className="flex flex-wrap items-start gap-5">
        <Avatar className="h-24 w-24">
          {contact.avatarUrl && <AvatarImage src={contact.avatarUrl} alt={name} />}
          <AvatarFallback className="text-2xl">{initials(name)}</AvatarFallback>
        </Avatar>

        <div className="min-w-[260px] flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold">{name}</h1>
            {m?.level && <Badge variant="success">{t('header.member', { level: m.level })}</Badge>}
            <StageBadge stage={contact.lifecycleStage} />
          </div>
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            {t('header.customerId')} <span className="font-mono">{contact.id.slice(0, 8).toUpperCase()}</span>
            <button type="button" onClick={copyId} aria-label={t('header.copyIdAria')} className="hover:text-foreground">
              <Copy className="h-3 w-3" />
            </button>
          </p>
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
            {contact.phone && <span>{contact.phone}</span>}
            {contact.email && <span>{contact.email}</span>}
            {contact.birthday && (
              <span>
                {dayjs(contact.birthday).format('DD/MM/YYYY')}
                {a != null ? ` (${t('header.age', { count: a })})` : ''}
              </span>
            )}
            {contact.jobTitle && <span>{contact.jobTitle}</span>}
          </div>
          <ContactTagPicker contactId={contact.id} tags={tags} />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Nhắn tin: sang màn nhắn tin — 1 kênh vào thẳng, nhiều kênh chọn kênh. Gọi điện: chưa có, để mờ. */}
          <Button className="gap-1.5" onClick={onMessage}>
            <MessageSquare className="h-4 w-4" /> {t('header.message')}
          </Button>
          <Button variant="outline" disabled className="gap-1.5 opacity-60" title={t('header.callSoonTitle')}>
            <Phone className="h-4 w-4" /> {t('header.call')} <span className="rounded bg-muted px-1 text-[10px] text-muted-foreground">{t('header.comingSoon')}</span>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button>
                {t('header.actions')} <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuItem onClick={onEdit}>
                <Pencil className="h-4 w-4" /> {t('header.editProfile')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onMessage}>
                <MessageCircle className="h-4 w-4" /> {plan.kind === 'none' ? t('header.startConversation') : t('header.openConversation')}
              </DropdownMenuItem>

              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[11px] font-normal text-muted-foreground">{t('header.care')}</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => onGoTab?.('notes')}>
                <NotebookPen className="h-4 w-4" /> {t('header.addNote')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onGoTab?.('notes')}>
                <CalendarPlus className="h-4 w-4" /> {t('header.addAppointment')}
              </DropdownMenuItem>
              {canAssign && (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <UserCog className="h-4 w-4" /> {t('header.assignOwner')}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="max-h-72 w-56 overflow-y-auto">
                    {members.map((u) => (
                      <DropdownMenuItem key={u.id} onClick={() => assign(u.id, u.fullName ?? t('header.staffLower'))} className={contact.assignedUserId === u.id ? 'font-semibold' : undefined}>
                        {u.fullName ?? t('header.staff')}{contact.assignedUserId === u.id && <span className="ml-auto text-[10px] text-muted-foreground">{t('header.current')}</span>}
                      </DropdownMenuItem>
                    ))}
                    {contact.assignedUserId && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => assign(null, '')}>{t('header.unassigned')}</DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              )}

              {!!manualRules?.length && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-[11px] font-normal text-muted-foreground">{t('header.automation')}</DropdownMenuLabel>
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <Play className="h-4 w-4" /> {t('header.runFlow')}
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="max-h-72 w-64 overflow-y-auto">
                      {manualRules.map((r) => (
                        <DropdownMenuItem key={r.id} onSelect={() => setRule(r)} className="flex-col items-start gap-0">
                          <span className="font-medium">{r.buttonLabel}</span>
                          {r.buttonLabel !== r.name && <span className="text-[11px] text-muted-foreground">{r.name}</span>}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                </>
              )}

              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[11px] font-normal text-muted-foreground">{t('header.data')}</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => setMergeOpen(true)}>
                <GitMerge className="h-4 w-4" /> {t('header.merge')}
              </DropdownMenuItem>
              <DropdownMenuItem disabled={!contact.phone} onClick={() => contact.phone && copy(contact.phone, t('header.phoneLabel'))}>
                <Copy className="h-4 w-4" /> {t('header.copyPhone')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={copyId}>
                <Copy className="h-4 w-4" /> {t('header.copyId')}
              </DropdownMenuItem>

              {canDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem disabled={deleting} className="text-destructive focus:text-destructive" onClick={onDelete}>
                    <Trash2 className="h-4 w-4" /> {t('header.deleteCustomer')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <ChayLuongHopXacNhan rule={rule} contactId={contact.id} tenKhach={name} onClose={() => setRule(null)} />
          <MergeContactDialog contact={contact} open={mergeOpen} onOpenChange={setMergeOpen} />
          <MessageContactDialog contactId={contact.id} name={name} conversations={threads} open={msgOpen} onOpenChange={setMsgOpen} />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-1.5" aria-label={t('header.stageAria')}>
        <span className="mr-1 text-xs text-muted-foreground">{t('header.stage')}</span>
        {stages.map((st, i) => {
          const curIdx = stages.findIndex((x) => x.key === contact.lifecycleStage)
          const cur = st.key === contact.lifecycleStage
          return (
            <button
              key={st.key}
              type="button"
              disabled={stageMove.pending}
              onClick={() => {
                if (cur) return
                stageMove.move(contact.id, st.key, { name })
              }}
              className={cn(
                'rounded-md border px-3 py-1 text-xs transition-colors',
                cur ? 'border-transparent font-semibold text-white' : i < curIdx && st.kind !== 'lost' ? 'border-transparent bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted',
              )}
              style={cur ? { backgroundColor: st.color ?? 'hsl(var(--primary))' } : undefined}
            >
              {st.label}
            </button>
          )
        })}
        {canManageStages && (
          <button type="button" onClick={openStageManager} className="ml-1 text-xs text-primary hover:underline">{t('header.managePipeline')}</button>
        )}
      </div>

      {ov?.lastOrderAt && dayjs().diff(dayjs(ov.lastOrderAt), 'day') >= 30 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg bg-primary/10 px-3 py-2 text-sm">
          <Trans t={t} i18nKey="header.winbackHint" count={dayjs().diff(dayjs(ov.lastOrderAt), 'day')} components={{ b: <b /> }} />
          <ChayLuongThuCong contactId={contact.id} tenKhach={name} />
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-3">
        <Kpi label={t('header.kpi.totalSpent')} value={formatVnd(m?.totalSpent ?? ov?.totalSpent ?? 0)} hint={ov?.spendChangePct != null ? t('header.kpi.spendChange', { arrow: ov.spendChangePct >= 0 ? '↑' : '↓', pct: Math.abs(ov.spendChangePct) }) : undefined} tone={ov?.spendChangePct != null && ov.spendChangePct < 0 ? 'down' : 'up'} />
        <Kpi label={t('header.kpi.orders')} value={String(ov?.orderCount ?? 0)} hint={ov?.lastOrderAt ? t('header.kpi.lastOrder', { date: dayjs(ov.lastOrderAt).format('DD/MM/YYYY') }) : undefined} />
        <Kpi label={t('header.kpi.avgOrder')} value={formatVnd(ov?.avgOrder ?? 0)} />
        <Kpi label={t('header.kpi.points')} value={m?.point != null ? new Intl.NumberFormat(currentLocale()).format(m.point) : '—'} hint={m ? undefined : t('header.kpi.notLinked')} />
      </div>
    </div>
  )
}
